import React, { useState } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import { companyService, alertService } from '../../services';

const ClaimProfileButton = ({ company: companyData, user, slug }) => {
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    work_email: '',
    title_in_company: '',
  });
  const [errors, setErrors] = useState({});

  const { company, verified, company_id } = companyData || {};

  if (verified || !company_id || company_id <= 0) {
    return null;
  }

  if (!user) {
    const loginUrl = `/auth/login?returnTo=/company/${slug}`;
    return (
      <Button variant="outline-primary" size="sm" href={loginUrl} rel="nofollow" className="my-3">
        Login to Claim Profile
      </Button>
    );
  }

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => {
    setShowModal(false);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required.';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required.';
    if (!formData.work_email.trim()) {
      newErrors.work_email = 'Work email is required.';
    } else {
      const freeEmailDomains = [
        'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 'icloud.com', 'live.com', 'msn.com'
      ];
      const domain = formData.work_email.split('@')[1];
      if (freeEmailDomains.includes(domain)) {
        newErrors.work_email = 'Please use a corporate email address. Personal emails are not allowed.';
      }
    }
    if (!formData.title_in_company.trim()) newErrors.title_in_company = 'Your title or role is required.';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClaimSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const claimData = { ...formData, user_id: user.user_id };
      await companyService.claimProfile(company_id, claimData);
      alertService.success('Your claim request has been submitted for review.');
      handleCloseModal();
    } catch (error) {
      if (error?.status === 409) {
        alertService.warn('You have already submitted a claim for this company.');
      } else {
        alertService.error(error?.message || 'An unexpected error occurred.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button variant="outline-primary" size="sm" onClick={handleShowModal} className="my-3">
        Request to Claim Profile
      </Button>

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Claim "{company?.company_name}"</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>To verify your position, please provide the following details.</p>
          <Form noValidate>
            <Form.Group className="mb-3" controlId="formFirstName">
              <Form.Label>First Name</Form.Label>
              <Form.Control type="text" name="first_name" value={formData.first_name} onChange={handleInputChange} isInvalid={!!errors.first_name} placeholder="Enter your first name" />
              <Form.Control.Feedback type="invalid">{errors.first_name}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" controlId="formLastName">
              <Form.Label>Last Name</Form.Label>
              <Form.Control type="text" name="last_name" value={formData.last_name} onChange={handleInputChange} isInvalid={!!errors.last_name} placeholder="Enter your last name" />
              <Form.Control.Feedback type="invalid">{errors.last_name}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" controlId="formWorkEmail">
              <Form.Label>Work Email</Form.Label>
              <Form.Control type="email" name="work_email" value={formData.work_email} onChange={handleInputChange} isInvalid={!!errors.work_email} placeholder="Enter your work email" />
              <Form.Control.Feedback type="invalid">{errors.work_email}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" controlId="formTitle">
              <Form.Label>Title / Role in Company</Form.Label>
              <Form.Control type="text" name="title_in_company" value={formData.title_in_company} onChange={handleInputChange} isInvalid={!!errors.title_in_company} placeholder="e.g., CEO, Marketing Manager" />
              <Form.Control.Feedback type="invalid">{errors.title_in_company}</Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleClaimSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Confirm Claim'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ClaimProfileButton;
